// src/pages/admin/GameEditBanner.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
  uploadBytes
} from 'firebase/storage';
import { db } from '../../firebase';

export default function GameEditBanner() {
  const { gameId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    bannerFile: null,
    bannerPreview: '',
    title: '',
    price: '',
    discountRate: '',
    releaseDate: '',
    comingSoon: false,
  });

  // PNG/JPEG → WebP 변환
  const convertToWebp = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              blob ? resolve(blob) : reject(new Error('WebP 변환 실패'));
            },
            'image/webp',
            0.8
          );
        };
        img.onerror = () => reject(new Error('이미지 로딩 실패'));
        img.src = reader.result;
      };
      reader.onerror = () => reject(new Error('FileReader 실패'));
      reader.readAsDataURL(file);
    });

  // 업로드 후 다운로드 가능한 URL 반환
  const uploadAndGetUrl = async (blobOrFile, folderPath, filename, contentType) => {
    const storage = getStorage();
    const fileRef = storageRef(storage, `${folderPath}/${filename}`);
    await uploadBytes(fileRef, blobOrFile, {
      contentType,
      cacheControl: 'public, max-age=43200',
    });
    return getDownloadURL(fileRef);
  };

  // 초기 데이터 로드
  useEffect(() => {
    (async () => {
      try {
        const refDoc = doc(db, 'games', gameId);
        const snap = await getDoc(refDoc);
        if (!snap.exists()) {
          alert('해당 게임을 찾을 수 없습니다.');
          navigate('/admin', { state: { tab: 'games' } });
          return;
        }
        const data = snap.data();

        let previewUrl = data.bannerImageUrl || '';
        if (previewUrl && !previewUrl.startsWith('http')) {
          previewUrl = await getDownloadURL(
            storageRef(getStorage(), previewUrl)
          );
        }

        setForm({
          bannerFile: null,
          bannerPreview: previewUrl,
          title: data.title || '',
          price: String(data.price ?? ''),
          discountRate: String(data.discountRate ?? ''),
          releaseDate: data.releaseDate || '',
          comingSoon: !!data.comingSoon,
        });
      } catch (err) {
        console.error(err);
        alert('데이터 불러오기 중 오류가 발생했습니다.');
        navigate('/admin', { state: { tab: 'games' } });
      } finally {
        setLoading(false);
      }
    })();
  }, [gameId, navigate]);

  const clearForm = useCallback(() => {
    navigate('/admin', { state: { tab: 'games' } });
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({
      ...f,
      bannerFile: file,
      bannerPreview: URL.createObjectURL(file),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      let bannerImageUrl = form.bannerPreview;

      if (form.bannerFile) {
        const webpBlob = await convertToWebp(form.bannerFile);
        const webpFilename = `${gameId}_banner.webp`;
        bannerImageUrl = await uploadAndGetUrl(
          webpBlob,
          `games/${gameId}`,
          webpFilename,
          'image/webp'
        );
      }

      const gameDocRef = doc(db, 'games', gameId);
      await updateDoc(gameDocRef, {
        bannerImageUrl,
        title: form.title,
        price: Number(form.price),
        discountRate: Number(form.discountRate),
        releaseDate: form.releaseDate,
        comingSoon: form.comingSoon,
      });

      const updatedSnap = await getDoc(gameDocRef);
      console.log('Updated Firestore data:', updatedSnap.data());

      alert('배너 정보가 업데이트되었습니다.');
      navigate('/admin', { state: { tab: 'games' } });
    } catch (err) {
      console.error(err);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-white">로딩 중…</div>;
  }

  const numericPrice = Number(form.price) || 0;
  const numericDiscount = Math.min(
    Math.max(Number(form.discountRate) || 0, 0),
    100
  );
  const discountedPrice = Math.round(
    (numericPrice * (100 - numericDiscount)) / 100
  );

  let previewPriceDisplay;
  if (!form.comingSoon && numericPrice === 0 && numericDiscount === 0) {
    previewPriceDisplay = (
      <span className="text-green-400 font-semibold">무료</span>
    );
  } else if (form.comingSoon && numericPrice === 0 && numericDiscount === 0) {
    previewPriceDisplay = (
      <span className="text-yellow-300 font-semibold">출시예정</span>
    );
  } else if (numericDiscount > 0) {
    previewPriceDisplay = (
      <>
        <span className="text-red-400 font-semibold">
          -{numericDiscount}%
        </span>
        <span className="line-through text-gray-500">
          ₩{numericPrice.toLocaleString()}
        </span>
        <span className="text-white font-bold">
          ₩{discountedPrice.toLocaleString()}
        </span>
      </>
    );
  } else {
    previewPriceDisplay = (
      <span className="text-white font-bold">
        ₩{numericPrice.toLocaleString()}
      </span>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] text-gray-100 py-10">
      <div className="max-w-5xl mx-auto px-4 space-y-12">
        <h1 className="text-3xl font-bold text-center">배너 편집</h1>
        <div className="bg-[#1a1a1a] rounded-2xl overflow-hidden divide-x divide-neutral-700">
          <div className="flex flex-col lg:flex-row">
            {/* Preview */}
            <div className="flex-none p-12 flex justify-center">
              <div className="w-[340px] h-[340px] rounded-2xl overflow-hidden bg-[#1f1f1f]">
                <div className="h-[200px] bg-gray-800">
                  {form.bannerPreview ? (
                    <img
                      src={form.bannerPreview}
                      alt="배너 미리보기"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-700">
                      <span className="text-gray-500">배너 없음</span>
                    </div>
                  )}
                </div>
                <div className="p-4 bg-[#27273c] flex flex-col justify-between h-[140px]">
                  <div>
                    <h3 className="text-xl font-bold text-white truncate">
                      {form.title || '제목 없음'}
                    </h3>
                    {form.comingSoon && form.releaseDate && (
                      <div className="text-sm text-gray-400">
                        출시 예정 {form.releaseDate}
                      </div>
                    )}
                  </div>
                  <div className="text-xl flex items-center justify-end space-x-2">
                    {previewPriceDisplay}
                  </div>
                </div>
              </div>
            </div>
            {/* Form */}
            <div className="flex-1 p-12">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="flex items-center mb-4">
                  <label className="w-28 font-medium">배너 이미지</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="flex-1 bg-gray-700 text-gray-200 rounded px-3 py-2"
                  />
                </div>
                <div className="flex items-center mb-4">
                  <label className="w-28 font-medium">제목</label>
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className="flex-1 bg-gray-700 text-gray-100 border border-gray-600 rounded px-3 py-2"
                  />
                </div>
                <div className="flex items-center mb-4">
                  <label className="w-28 font-medium">가격</label>
                  <input
                    name="price"
                    type="number"
                    min="0"
                    value={form.price}
                    onChange={handleChange}
                    className="flex-1 bg-gray-700 text-gray-100 border border-gray-600 rounded px-3 py-2"
                  />
                </div>
                <div className="flex items-center mb-4">
                  <label className="w-28 font-medium">할인율 (%)</label>
                  <input
                    name="discountRate"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0~100"
                    value={form.discountRate}
                    onChange={(e) => {
                      let v = Number(e.target.value);
                      if (isNaN(v)) v = 0;
                      v = Math.min(Math.max(v, 0), 100);
                      handleChange({
                        target: { name: 'discountRate', value: String(v) },
                      });
                    }}
                    className="flex-1 bg-gray-700 text-gray-100 border border-gray-600 rounded px-3 py-2"
                  />
                </div>
                <div className="flex items-center mb-6">
                  <label className="w-28 font-medium">출시일</label>
                  <input
                    name="releaseDate"
                    type="text"
                    placeholder="YYYY-MM-DD"
                    value={form.releaseDate}
                    onChange={handleChange}
                    disabled={!form.comingSoon}
                    className={`flex-1 bg-gray-700 text-gray-100 border border-gray-600 rounded px-3 py-2 ${
                      !form.comingSoon ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div className="flex items-center mb-4">
                  <label className="w-28 font-medium">출시 예정</label>
                  <input
                    name="comingSoon"
                    type="checkbox"
                    checked={form.comingSoon}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-500 bg-gray-700 border-gray-600 rounded"
                  />
                </div>
                <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={clearForm}
                    disabled={submitting}
                    className="bg-gray-600 text-gray-200 px-5 py-2 rounded hover:bg-gray-500 disabled:opacity-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-500"
                  >
                    {submitting ? '저장중…' : '저장'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
